import { Decimal, ZERO } from '@snap-types/types/decimal';

import type { QuantityAwareConfig, QuantityAwareInputs } from '../types';
import { baseLeadTimeCalculation } from './base-lead-time';
import { calculateOperationHours } from './operation-lead-time';

export type QuantityAwareOperationBreakdown = {
  sequenceNo: number;
  setupHours: Decimal | null;
  runHours: Decimal | null;
  transitDays: Decimal | null;
  totalHours: Decimal;
};

export type QuantityAwareLeadTimeBreakdown = {
  operations: QuantityAwareOperationBreakdown[];
  /** Setup and run hours across every operation. */
  productionHours: Decimal;
  /** `productionHours` converted at the site's shift hours. */
  productionDays: Decimal;
  /** The site's move days for this operation count. */
  moveDays: number;
  /** The site's buffer days. */
  bufferDays: number;
  /** `moveDays` and `bufferDays` together. */
  moveAndBufferDays: number;
  /** Shop floor time, converted at the site's working week. */
  shopWeeks: Decimal;
  /** Transit days across every operation. */
  transitDays: Decimal;
  /** `transitDays` converted at the week the site records transit on. */
  transitWeeks: Decimal;
  /** The greater of the part's resource and material lead times, in weeks. */
  baseWeeks: Decimal;
  /** The whole-week total a quote is given in, and what we store. */
  totalWeeks: number;
};

/**
 * Breaks a quantity-aware lead time into the parts that produce it. This is the
 * calculation `calculateTotalLeadTime` performs, kept in one place so the
 * explanation in the UI cannot drift from the stored value.
 *
 * A lead time is three waits that do not share a week. Shop floor time — setup,
 * run, move, and buffer — runs on the days the site produces. Outside service
 * transit runs on the carrier's week. The material and resource wait runs on
 * the week Visual's planning lead times are kept in. Converting each on its own
 * basis and adding the weeks is the whole point of this shape: a single divisor
 * would charge a five day site for weekends it never worked.
 *
 * Returns null under the same conditions the total does: no base lead time, or
 * no operations.
 */
export function calculateLeadTimeBreakdown(
  quantity: Decimal,
  {
    resourceLeadTimeWeeks,
    materialLeadTimeDays,
    operations,
  }: QuantityAwareInputs,
  {
    shiftHours,
    bufferDays,
    moveDayRatio,
    productionDaysPerWeek,
    transitDaysPerWeek,
    planningDaysPerWeek,
  }: QuantityAwareConfig,
): QuantityAwareLeadTimeBreakdown | null {
  const baseWeeks = baseLeadTimeCalculation(
    { resourceLeadTimeWeeks, materialLeadTimeDays },
    { planningDaysPerWeek },
  );

  if (baseWeeks === null || operations.length === 0) {
    return null;
  }

  const operationBreakdowns = operations.map((operation) => {
    const { setupHours, runHours } = calculateOperationHours({
      quantity,
      ...operation,
    });

    return {
      sequenceNo: operation.sequenceNo,
      setupHours,
      runHours,
      // A negative transit day is a typo in the routing, not a head start, and
      // is dropped here as well as from the total so the rows of the breakdown
      // still add up to the total it explains.
      transitDays: operation.transitDays?.gt(ZERO)
        ? operation.transitDays
        : null,
      totalHours: Decimal.sum(setupHours ?? ZERO, runHours ?? ZERO),
    };
  });

  const productionHours = Decimal.sum(
    ...operationBreakdowns.map((operation) => operation.totalHours),
  );
  const productionDays = productionHours.dividedBy(shiftHours);
  // Move days are whole days on the floor, so a fractional ratio rounds up
  // rather than riding along as a fraction into the total.
  const moveDays = moveDayRatio.times(operations.length).ceil().toNumber();
  const moveAndBufferDays = moveDays + bufferDays;

  const shopWeeks = productionDays
    .plus(moveAndBufferDays)
    .dividedBy(productionDaysPerWeek);

  const transitDays = Decimal.sum(
    ZERO,
    ...operationBreakdowns.map((operation) => operation.transitDays ?? ZERO),
  );
  const transitWeeks = transitDays.dividedBy(transitDaysPerWeek);

  const weeks = shopWeeks.plus(transitWeeks).plus(baseWeeks);

  return {
    operations: operationBreakdowns,
    productionHours,
    productionDays,
    moveDays,
    bufferDays,
    moveAndBufferDays,
    shopWeeks,
    transitDays,
    transitWeeks,
    baseWeeks,
    /**
     * A partial week is a week the customer waits, so the sum rounds up. This
     * is the only rounding between the three terms and the stored value —
     * lead times are stored in weeks, so nothing converts back into days.
     */
    totalWeeks: weeks.ceil().toNumber(),
  };
}
